<script lang="ts" setup>
import { useRoute, useRouter } from 'vue-router'
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { APKTracking } from '@/utils/event-tracking'
import { getAssetsFile } from '@/utils/pub-use'
import useUIStore from '@/stores/modules/ui'

defineOptions({ name: 'IFrame' })

const loading = ref(true)
const height = ref('')
const frameRef = ref<HTMLIFrameElement | null>(null)
const route = useRoute()
const router = useRouter()
const uiStore = useUIStore()

// 多语言支持
const { t } = useI18n()

// 使用全局 socket 实例（与项目中其他地方保持一致）
const socket = window.$socket

// 倒计时相关
const countdown = ref('')
const countdownTimer = ref<NodeJS.Timeout | null>(null)

// 维护弹窗相关
const showMaintenanceModal = ref(false)

// 格式化时间显示
const formatTime = (milliseconds: number): string => {
  const totalSeconds = Math.floor(milliseconds / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  
  if (hours > 0) {
    return t('maintenance.timeFormat.hours', { hours, minutes, seconds })
  } else if (minutes > 0) {
    return t('maintenance.timeFormat.minutes', { minutes, seconds })
  } else {
    return t('maintenance.timeFormat.seconds', { seconds })
  }
}

// 开始倒计时
const startCountdown = (targetTime: number) => {
  const updateCountdown = () => {
    const now = Date.now()
    const remaining = targetTime - now
    
    if (remaining <= 0) {
      countdown.value = t('maintenance.started')
      if (countdownTimer.value) {
        clearInterval(countdownTimer.value)
        countdownTimer.value = null
      }
      // 维护开始后30秒才显示弹窗
      setTimeout(() => {
        showMaintenanceModal.value = true
      }, 30000) // 30秒 = 30000毫秒
      return
    }
    
    countdown.value = formatTime(remaining)
  }
  
  // 立即更新一次
  updateCountdown()
  
  // 每秒更新一次
  countdownTimer.value = setInterval(updateCountdown, 1000)
}

// 停止倒计时
const stopCountdown = () => {
  if (countdownTimer.value) {
    clearInterval(countdownTimer.value)
    countdownTimer.value = null
  }
  countdown.value = ''
}

// 监听 maintenance 事件
const handleMaintenance = (data: any) => {
  console.log('收到 maintenance 事件:', data)
  
  if (data && data.start) {
    const startTime = data.start
    const now = Date.now()
    
    // 检查维护是否已开始
    if (startTime <= now) {
      // 维护已开始，30秒后显示弹窗
      setTimeout(() => {
        showMaintenanceModal.value = true
      }, 20000) // 30秒 = 30000毫秒
      return
    }
    
    // 维护还未开始，显示倒计时到开始时间
    startCountdown(startTime)
    
    // 显示维护提示弹窗
    const remainingTime = startTime - now
    const timeText = formatTime(remainingTime)
    ;(window as any).$toast(t('maintenance.willStart', { time: timeText }))
    
    // 每30秒显示一次倒计时更新
    const showCountdownUpdate = () => {
      if (countdown.value && countdown.value !== t('maintenance.started')) {
        ;(window as any).$toast(t('maintenance.remainingTime', { time: countdown.value }))
      }
    }
    
    // 30秒后开始定期显示倒计时
    setTimeout(() => {
      const updateInterval = setInterval(() => {
        if (countdown.value && countdown.value !== t('maintenance.started')) {
          showCountdownUpdate()
        } else {
          clearInterval(updateInterval)
        }
      }, 30000) // 每30秒显示一次
    }, 30000)
  }
}

// 处理维护弹窗确认
const handleMaintenanceConfirm = () => {
  showMaintenanceModal.value = false
  // 跳转到404页面
  router.push('/404')
}

function init() {
  height.value = `${document.documentElement.clientHeight}px`
  loading.value = false
}

function updateFrameHeight() {
  if (frameRef.value) {
    window.requestAnimationFrame(() => {
      height.value = `${document.documentElement.clientHeight}px`
    })
  }
}

function goToHome() {
  // 获取来源页面信息
  const referrer = document.referrer
  const fromPath = sessionStorage.getItem('fromPath')

  // 如果是从 /gameLimit 页面过来的，直接返回首页
  if (fromPath === '/gameLimit') {
    router.replace('/')
    sessionStorage.removeItem('fromPath')
    return
  }

  // 如果是从 findGameByVendor 页面过来的，跳转到首页并打开侧边菜单栏
  if (fromPath && fromPath.includes('/findGameByVendor')) {
    router.replace('/').then(() => {
      // 使用 nextTick 确保页面渲染完成后再打开侧边栏
      nextTick(() => {
        uiStore.openSidebar()
      })
    })
    sessionStorage.removeItem('fromPath')
    return
  }

  // 如果有记录来源路径，优先返回来源页面
  if (fromPath) {
    router.replace(fromPath)
    sessionStorage.removeItem('fromPath')
  } else if (window.history.length > 1) {
    // 否则使用浏览器返回
    router.back()
  } else {
    // 最后兜底到首页
    router.replace('/')
  }
}

// 修改屏幕方向检测
const isLandscape = ref(window.orientation === 90 || window.orientation === -90)

// 修改屏幕方向变化处理函数
function handleOrientationChange() {
  isLandscape.value = window.orientation === 90 || window.orientation === -90
}

onMounted(() => {
  setTimeout(() => {
    init()
    window.addEventListener('resize', updateFrameHeight)
    window.addEventListener('orientationchange', handleOrientationChange)
  }, 300)

  // 初始化时也检查一次
  handleOrientationChange()

  // 设置 socket 监听
  console.log('IFrame 组件挂载，开始设置 socket 监听')
  console.log('Socket 实例:', socket)
  console.log('Socket 连接状态:', socket?.isConnected())
  console.log('Socket 认证状态:', socket?.isAuthenticated())
  
  if (socket) {
    // 先移除之前的事件监听器，避免重复注册
    socket.off('maintenance')
    // 监听 maintenance 事件
    socket.on('maintenance', handleMaintenance)
    console.log('✅ maintenance 事件监听器已设置')
    
    // 测试监听器是否正常工作 - 监听一个已知事件
    socket.on('connect', () => {
      console.log('Socket 连接成功 - 在 IFrame 组件中')
    })
  } else {
    console.error('❌ Socket 实例不存在')
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', updateFrameHeight)
  window.removeEventListener('orientationchange', handleOrientationChange)
  
  // 清理 socket 监听
  if (socket) {
    socket.off('maintenance', handleMaintenance)
    console.log('✅ maintenance 事件监听器已清理')
  }
  
  // 清理倒计时定时器
  stopCountdown()
})

// 计算响应式初始位置 - 再往左移动100px
const getInitialPosition = () => {
  const screenWidth = window.innerWidth
  const screenHeight = window.innerHeight

  // 判断是否为PC端（宽度大于768px）
  const isPC = screenWidth >= 768

  if (isPC) {
    // PC端：显示在页面内容区域左侧，再往左移动100px
    // 假设页面内容区域宽度为375px（移动端标准宽度），居中显示
    const contentWidth = 375
    const contentLeft = (screenWidth - contentWidth) / 2

    if (screenWidth >= 1440) {
      // 超大屏幕 - 再往右移动50px
      return { x: contentLeft - 40, y: 40 }
    } else if (screenWidth >= 1024) {
      // 大屏幕 - 再往右移动50px
      return { x: contentLeft - 38, y: 35 }
    } else {
      // PC端 - 再往右移动50px
      return { x: contentLeft - 35, y: 30 }
    }
  } else {
    // 移动端：显示在屏幕左侧 - 保持原位置
    if (screenWidth >= 1440) {
      return { x: 30, y: 40 }
    } else if (screenWidth >= 1024) {
      return { x: 25, y: 35 }
    } else if (screenWidth >= 768) {
      return { x: 20, y: 30 }
    } else {
      return { x: 15, y: 25 }
    }
  }
}

// 按钮位置状态 - 使用响应式初始位置
const buttonPosition = ref(getInitialPosition())

// 计算按钮样式
const buttonStyle = computed(() => ({
  left: `${buttonPosition.value.x}px`,
  top: `${buttonPosition.value.y}px`,
  transform: isDragging.value ? 'none' : 'translate(0, 0)',
  transition: isDragging.value ? 'none' : 'transform 0.3s ease-out',
}))

// 更新按钮位置以适应屏幕变化
const updateButtonPosition = () => {
  const newPosition = getInitialPosition()
  buttonPosition.value = newPosition
}

// 监听屏幕尺寸变化
onMounted(() => {
  window.addEventListener('resize', updateButtonPosition)
  window.addEventListener('orientationchange', updateButtonPosition)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateButtonPosition)
  window.removeEventListener('orientationchange', updateButtonPosition)
})

// 按钮位置状态
const isDragging = ref(false)
const dragOffset = ref({ x: 0, y: 0 })
const lastTouch = ref({ x: 0, y: 0 })
const velocity = ref({ x: 0, y: 0 })
const lastTime = ref(0)
const touchStartTime = ref(0)
const touchStartPosition = ref({ x: 0, y: 0 })

// 仅首次上报进入游戏
const hasReportedEnterGame = ref(false)

// 归一化 iframe src（route.query.rid 可能是 string | string[]）
const iframeSrc = computed(() => {
  const rid = route.query.rid as string | string[] | undefined
  return Array.isArray(rid) ? (rid[0] || '') : (rid || '')
})

function handleFrameLoad(): void {
  if (hasReportedEnterGame.value)
    return

  const apkTracking = new APKTracking()
  const src = iframeSrc.value
  const params: Record<string, any> = {}
  if (src)
    params.url = src

  apkTracking.trackEnterGame(params)
  const adjustTracking = window.$adjustTracking
  if (adjustTracking) {
    adjustTracking.trackEnterGame()
  }
  const appsFlyerTracking = window.$appsFlyerTracking
  if (appsFlyerTracking) {
    appsFlyerTracking.trackEnterGame()
  }
  if (typeof window.fbq === 'function') {
    window.fbq('track', 'EnterGame')
  }
  hasReportedEnterGame.value = true
}

// 开始拖动
function startDrag(e: TouchEvent) {
  isDragging.value = true
  const touch = e.touches[0]
  lastTouch.value = { x: touch.clientX, y: touch.clientY }
  lastTime.value = Date.now()
  touchStartTime.value = Date.now()
  touchStartPosition.value = { x: touch.clientX, y: touch.clientY }
  dragOffset.value = {
    x: touch.clientX - buttonPosition.value.x,
    y: touch.clientY - buttonPosition.value.y,
  }

  document.addEventListener('touchmove', onDrag, { passive: true })
  document.addEventListener('touchend', stopDrag)
}

// 处理触摸结束
function handleTouchEnd() {
  const touchEndTime = Date.now()
  const touchDuration = touchEndTime - touchStartTime.value

  // 如果触摸时间小于200ms且移动距离小于10px，认为是点击
  if (touchDuration < 200
    && Math.abs(touchStartPosition.value.x - lastTouch.value.x) < 10
    && Math.abs(touchStartPosition.value.y - lastTouch.value.y) < 10) {
    goToHome()
  }
}

// 拖动中 - 修改边界限制逻辑
function onDrag(e: TouchEvent) {
  if (!isDragging.value)
    return

  const touch = e.touches[0]
  const currentTime = Date.now()
  const deltaTime = currentTime - lastTime.value

  // 计算速度
  velocity.value = {
    x: (touch.clientX - lastTouch.value.x) / deltaTime,
    y: (touch.clientY - lastTouch.value.y) / deltaTime,
  }

  // 更新位置
  const newX = touch.clientX - dragOffset.value.x
  const newY = touch.clientY - dragOffset.value.y

  // 根据屏幕尺寸和设备类型计算边界限制
  const screenWidth = window.innerWidth
  const screenHeight = window.innerHeight
  const isPC = screenWidth >= 768

  let maxX, maxY, minX, minY

  if (isPC) {
    // PC端：限制在页面内容区域内，更靠左的边界
    const contentWidth = 375
    const contentLeft = (screenWidth - contentWidth) / 2
    const contentRight = contentLeft + contentWidth

    if (screenWidth >= 1440) {
      maxX = contentRight - 80
      maxY = screenHeight - 80
      minX = contentLeft - 45
      minY = 20
    } else if (screenWidth >= 1024) {
      maxX = contentRight - 70
      maxY = screenHeight - 70
      minX = contentLeft - 42
      minY = 15
    } else {
      maxX = contentRight - 60
      maxY = screenHeight - 60
      minX = contentLeft - 40
      minY = 10
    }
  } else {
    // 移动端：限制在整个屏幕内
    if (screenWidth >= 1440) {
      maxX = screenWidth - 80
      maxY = screenHeight - 80
      minX = 30
      minY = 20
    } else if (screenWidth >= 1024) {
      maxX = screenWidth - 70
      maxY = screenHeight - 70
      minX = 25
      minY = 15
    } else if (screenWidth >= 768) {
      maxX = screenWidth - 60
      maxY = screenHeight - 60
      minX = 20
      minY = 10
    } else {
      maxX = screenWidth - 55
      maxY = screenHeight - 55
      minX = 15
      minY = 5
    }
  }

  buttonPosition.value = {
    x: Math.max(minX, Math.min(newX, maxX)),
    y: Math.max(minY, Math.min(newY, maxY)),
  }

  lastTouch.value = { x: touch.clientX, y: touch.clientY }
  lastTime.value = currentTime
}

// 停止拖动 - 修改惯性效果的边界限制
function stopDrag() {
  isDragging.value = false
  document.removeEventListener('touchmove', onDrag)
  document.removeEventListener('touchend', stopDrag)

  // 添加惯性效果
  if (Math.abs(velocity.value.x) > 0.5 || Math.abs(velocity.value.y) > 0.5) {
    const inertia = () => {
      const newX = buttonPosition.value.x + velocity.value.x * 16
      const newY = buttonPosition.value.y + velocity.value.y * 16

      // 根据屏幕尺寸和设备类型计算边界限制
      const screenWidth = window.innerWidth
      const screenHeight = window.innerHeight
      const isPC = screenWidth >= 768

      let maxX, maxY, minX, minY

      if (isPC) {
        // PC端：限制在页面内容区域内，更靠左的边界
        const contentWidth = 375
        const contentLeft = (screenWidth - contentWidth) / 2
        const contentRight = contentLeft + contentWidth

        if (screenWidth >= 1440) {
          maxX = contentRight - 80
          maxY = screenHeight - 80
          minX = contentLeft - 45
          minY = 20
        } else if (screenWidth >= 1024) {
          maxX = contentRight - 70
          maxY = screenHeight - 70
          minX = contentLeft - 42
          minY = 15
        } else {
          maxX = contentRight - 60
          maxY = screenHeight - 60
          minX = contentLeft - 40
          minY = 10
        }
      } else {
        // 移动端：限制在整个屏幕内
        if (screenWidth >= 1440) {
          maxX = screenWidth - 80
          maxY = screenHeight - 80
          minX = 30
          minY = 20
        } else if (screenWidth >= 1024) {
          maxX = screenWidth - 70
          maxY = screenHeight - 70
          minX = 25
          minY = 15
        } else if (screenWidth >= 768) {
          maxX = screenWidth - 60
          maxY = screenHeight - 60
          minX = 20
          minY = 10
        } else {
          maxX = screenWidth - 55
          maxY = screenHeight - 55
          minX = 15
          minY = 5
        }
      }

      buttonPosition.value = {
        x: Math.max(minX, Math.min(newX, maxX)),
        y: Math.max(minY, Math.min(newY, maxY)),
      }

      // 减速
      velocity.value = {
        x: velocity.value.x * 0.95,
        y: velocity.value.y * 0.95,
      }

      // 当速度足够小时停止
      if (Math.abs(velocity.value.x) > 0.1 || Math.abs(velocity.value.y) > 0.1) {
        requestAnimationFrame(inertia)
      }
    }

    requestAnimationFrame(inertia)
  }
}
</script>

<template>
  <div v-loading="loading" class="iframe-container">
    <div class="button-container" @click="goToHome">
      <img
        :src="getAssetsFile('game/closes.png')" alt="" class="exit-fullscreen-button"
        :class="{ 'rotate-270': isLandscape }" :style="buttonStyle" @touchstart="startDrag"
        @touchend="handleTouchEnd"
      >
    </div>
    <iframe
      ref="frameRef" :src="iframeSrc" frameborder="no" scrolling="no"
      style="width: 100%; height: 100%; border: none; overflow: hidden;" allowfullscreen
      @load="handleFrameLoad"
    />
    
    <!-- 维护弹窗 -->
    <van-dialog
  v-model:show="showMaintenanceModal"
  :title="t('maintenance.title')"
  :message="t('maintenance.inProgressMessage')"
  :show-cancel-button="false"
  :show-confirm-button="true"
  :confirm-button-text="t('maintenance.confirm')"
  @confirm="handleMaintenanceConfirm"
  class="maintenance-dialog"
/>
  </div>
</template>

<style lang="css">
.iframe-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  background: #000;
  margin: 0;
  padding: 0;
  overflow: hidden;
  touch-action: none;
}

.button-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 100000;
  pointer-events: none;
}

.exit-fullscreen-button {
  position: fixed;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  z-index: 100001;
  pointer-events: auto;
  font-weight: bold;
  user-select: none;
  touch-action: none;
  font-size: 16px;
  /* 移动端默认尺寸 */
  width: 45px;
  height: 45px;
}

/* PC端尺寸调整 */
@media (min-width: 768px) {
  .exit-fullscreen-button {
    width: 45px;
    height: 45px;
  }
}

/* 大屏幕尺寸调整 */
@media (min-width: 1024px) {
  .exit-fullscreen-button {
    width: 45px;
    height: 45px;
  }
}

/* 超大屏幕尺寸调整 */
@media (min-width: 1440px) {
  .exit-fullscreen-button {
    width: 45px;
    height: 45px;
  }
}

.exit-fullscreen-button:active {
  transform: scale(0.95);
}

.exit-fullscreen-button.rotate-270 {
  transform: rotate(270deg);
}

.iframe-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  border: none;
  margin: 0;
  padding: 0;
}

.maintenance-dialog {
  z-index: 100002;
}
</style>
